import { useCallback, useRef } from "react";
import { MOBILE_VIEWPORT_MEDIA_QUERY } from "../utils/viewportBreakpoints";

const SWIPE_MIN_DISTANCE = 70;
const SWIPE_MAX_VERTICAL_DRIFT = 80;
const SWIPE_HORIZONTAL_RATIO = 1.5;

function isMobilePanelViewport() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY).matches;
}

function isEditableTouchTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

export function useSwipeToClose({ enabled = true, onClose } = {}) {
  return useHorizontalSwipe({
    enabled,
    direction: "left",
    onSwipe: onClose,
  });
}

export function useSwipeToOpen({ enabled = true, onOpen } = {}) {
  return useHorizontalSwipe({
    enabled,
    direction: "right",
    onSwipe: onOpen,
  });
}

function useHorizontalSwipe({ enabled = true, direction = "left", onSwipe } = {}) {
  const touchStartRef = useRef(null);

  const canSwipe = useCallback(
    () => enabled && typeof onSwipe === "function" && isMobilePanelViewport(),
    [enabled, onSwipe]
  );

  const onTouchStart = useCallback(
    (event) => {
      if (!canSwipe() || isEditableTouchTarget(event.target)) {
        touchStartRef.current = null;
        return;
      }
      const touch = event.touches?.[0];
      if (!touch) return;
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [canSwipe]
  );

  const onTouchEnd = useCallback(
    (event) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start || !canSwipe()) return;

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      const isExpectedDirection =
        direction === "right" ? deltaX >= SWIPE_MIN_DISTANCE : deltaX <= -SWIPE_MIN_DISTANCE;
      const isMostlyHorizontal =
        absDeltaY <= SWIPE_MAX_VERTICAL_DRIFT && absDeltaX >= absDeltaY * SWIPE_HORIZONTAL_RATIO;

      if (isExpectedDirection && isMostlyHorizontal) {
        onSwipe();
      }
    },
    [canSwipe, direction, onSwipe]
  );

  const onTouchCancel = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  return {
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
  };
}
